import React from "react";
import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import { FormGroup, Label, Button } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import axios from "../../services/api/api";
import Select from "react-select";
import Creatable from "react-select/creatable";
import { API_BASE_URL } from "../../constants/api.constants";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import { compareByName } from "../../Utilities/sortHelper";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

const UpdateClassForm = (props) => {
  const intl = useIntl();
  const { values, errors, touched, isSubmitting, dismissModal } = props;

  const handleChange = (selected) => {
    values.selectedTeachers = selected.map((t) =>
      values.teacherSelection.find((x) => x.id === t.value)
    );
  };

  const getPreselectedTeachers = () => {
    var teachers = [];
    if (!values.classToUpdate) return teachers;
    values.classToUpdate.teachers.forEach((c) => {
      teachers.push({
        value: c.id,
        label: c.name,
        clearableValue: true,
      });
    });
    return teachers;
  };

  const getPreselectedProgramme = () => {
    if (!values.classToUpdate) return null;
    return {
      label: values.classToUpdate.programme.name,
      value: values.classToUpdate.programme.id,
      clearableValue: true,
    };
  };

  const handleProgramChange = (selected) => {
    values.selectedProgramme = values.programmeSelection.find(
      (x) => x.id === selected.value
    );
  };

  const handleOrientationChange = (e) => {
    e
      ? (values.selectedOrientation = {
          id: typeof e.value === "string" ? 0 : e.value,
          name: e.label,
        })
      : (values.selectedOrientation = { id: 0, name: "" });
  };

  return (
    <div className="col-12 col-sm-12">
      <Form>
        <FormGroup>
          <Label>Klassnamn</Label>
          <Field
            className={`form-control ${
              errors.className && touched.className && "is-invalid"
            }`}
            type="input"
            name="className"
            placeholder={intl.formatMessage({ id: "ClassName" })}
            value={values.className}
          />
          {errors.className && touched.className && (
            <div className="invalid-feedback">{errors.className}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="selectTeachers">
            <FormattedMessage id="SelectOneOrMoreTeachers" />
          </Label>
          <Select
            placeholder={intl.formatMessage({ id: "ChooseTeacherPlaceholder" })}
            isMulti
            noOptionsMessage={() => intl.formatMessage({ id: "NoSelectableTeachersInClass" })}
            onChange={(selected) => handleChange(selected)}
            defaultValue={getPreselectedTeachers()}
            options={[...values.teacherSelection]
              .sort(compareByName)
              .map((option) => ({
                ...option,
                label: option.name,
                value: option.id,
                clearableValue: true,
              }))}
          />
        </FormGroup>
        <FormGroup>
          <Label for="selectProgramme">
            <FormattedMessage id="ChooseProgram" />
          </Label>
          <Select
            placeholder={intl.formatMessage({ id: "ChooseProgramPlaceholder" })}
            noOptionsMessage={() => intl.formatMessage({ id: "NoSelectablePrograms" })}
            onChange={(e) => handleProgramChange(e)}
            defaultValue={getPreselectedProgramme()}
            options={
              values.programmeSelection.length
                ? [...values.programmeSelection]
                    .sort(compareByName)
                    .map((option) => ({
                      ...option,
                      label: option.name,
                      value: option.id,
                      clearableValue: true,
                    }))
                : []
            }
          />

          {errors.selectedProgramme && touched.selectedProgramme && (
            <div className="invalid-feedback">{errors.selectedProgramme}</div>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="selectOrientation">
            <FormattedMessage id="ChooseOrientation" />{" "}
            <small>
              <FormattedMessage id="OrientationCreateHint" />
            </small>
          </Label>
          <Creatable
            name="selectedOrientation"
            isClearable
            placeholder={intl.formatMessage({ id: "ChooseOrientationPlaceholder" })}
            noOptionsMessage={() => intl.formatMessage({ id: "NoSelectablePrograms" })}
            onChange={(e) => handleOrientationChange(e)}
            defaultValue={
              values.classToUpdate && values.classToUpdate.orientation
                ? {
                    label: values.classToUpdate.orientation.name,
                    value: values.classToUpdate.orientation.id.toString(),
                    clearableValue: true,
                  }
                : null
            }
            options={[...values.orientationSelection]
              .sort(compareByName)
              .map((option) => ({
                ...option,
                label: option.name,
                value: option.id.toString(),
                clearableValue: true,
              }))}
          />

          {errors.selectedOrientation && touched.selectedOrientation && (
            <div className="invalid-feedback">{errors.selectedOrientation}</div>
          )}
        </FormGroup>
        <div className="d-flex justify-content-end gap-2">
          <Button
            color="secondary"
            outline
            type="button"
            onClick={dismissModal}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button color="primary" outline type="submit" disabled={isSubmitting}>
            <FormattedMessage id={isSubmitting ? "Wait" : "Save"} />
          </Button>
        </div>
      </Form>
    </div>
  );
};

const FormikForm = withFormik({
  mapPropsToValues: (props) => ({
    classToUpdate: props.classToUpdate,
    className: props.classToUpdate ? props.classToUpdate.name : "",
    teacherSelection: props.teacherSelection,
    programmeSelection: props.programmeSelection,
    orientationSelection: props.orientationSelection
      ? props.orientationSelection
      : [],
    selectedTeachers: props.classToUpdate ? props.classToUpdate.teachers : [],
    selectedProgramme: props.classToUpdate ? props.classToUpdate.programme : [],
    selectedOrientation:
      props.classToUpdate && props.classToUpdate.orientation
        ? props.classToUpdate.orientation
        : null,
  }),

  validationSchema: Yup.object().shape({
    className: Yup.string().required(
      <FormattedMessage id="Views.Teacher.CreateClassForm.Error.RequireName" />
    ),
    selectedProgramme: Yup.mixed().required(
      <FormattedMessage id="Views.Teacher.CreateClassForm.Error.RequireProgram" />
    ),
    // selectedOrientation: Yup.mixed().required(
    //   <FormattedMessage id="Views.Teacher.CreateClassForm.Error.RequireName" />
    // )
  }),

  handleSubmit(values, { props, resetForm, setSubmitting }) {
    axios
      .post(`${API_BASE_URL}/Class/UpdateClass`, {
        id: values.classToUpdate.id,
        name: values.className,
        school: values.school,
        programme: values.selectedProgramme,
        orientation: values.selectedOrientation,
        teachers: values.selectedTeachers,
      })
      .then((res) => {
        toast.success("Klassen har uppdaterats", TOAST_CONFIG);
        resetForm();
        props.reloadAfterProceed(res.data.result);
        props.dismissModal();
      })
      .catch((err) => {
        toast.error(
          formatServiceError(props.intl, err?.response?.data) || err.message,
          TOAST_CONFIG,
        );
      });
    setSubmitting(false);
  },
})(UpdateClassForm);

// withFormik-konfigurationen kan inte använda hooks, och den här versionen av react-intl
// har ingen injectIntl. Wrappern skickar in intl som prop så handleSubmit kommer åt den.
const UpdateClassFormWithIntl = (props) => {
  const intl = useIntl();

  return <FormikForm {...props} intl={intl} />;
};

export default UpdateClassFormWithIntl;
